/* ==========================================================================
   DR. PATKAR'S - GLOBAL MOTION SYSTEM (dp-motion.js)
   --------------------------------------------------------------------------
   1. Unified scroll reveal for every section on every page.
   2. Cart feedback: the header badge bumps when the count goes up.

   Safety
   - Runs once (guarded), no globals except window.DPMotion.
   - Only content that starts BELOW the fold is ever hidden, so the hero,
     product info, prices and Add to cart are never delayed.
   - Reduced motion or the theme editor: reveal is switched off entirely.
   - Sections with their own reveal turned on, Dawn's scroll-trigger
     sections, the product page purchase area and the header/footer groups
     are left alone.
   - Reveal attributes are removed as soon as the animation ends, so no
     transform / opacity / stacking context is left behind on any element.
   - Theme editor section reloads are handled without double binding.
   ========================================================================== */
(function () {
  'use strict';

  if (window.DPMotion) return;

  var docEl = document.documentElement;
  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  /* ------------------------------------------------------------------------
     Registry
     ------------------------------------------------------------------------ */

  /* Repeated items that reveal with a short, capped stagger */
  var ITEM = [
    '.sg__card', '.sc-shop__card', '.rvp__card', '.ws__card',
    '.fbc__card', '.lb__card', '.rp__card', '.cl__card',
    '.bac__card', '.ntp__card', '.mvc__card', '.ci__card', '.cf__card',
    '.um__card', '.wc__chip', '.dpj__counter', '.op__feature',
    '.bc__feature', '.dpc__feature', '.fq__item', '.bp__card',
    '.bp__why-item', '.bp__step'
  ].join(',');

  /* Sections that must never move */
  var SKIP_SECTION = [
    '.shopify-section-group-header-group',
    '.shopify-section-group-footer-group',
    '[data-dp-motion-skip]',
    '.hero-dr-patkars',          /* hero has its own cinematic entrance   */
    '.ppd',                      /* product purchase area: always instant */
    '.dpbc',                     /* breadcrumbs: utility                  */
    '.scroll-trigger',           /* Dawn sections use Dawn's own reveal   */
    '.shopify-app-block',
    'cart-drawer', 'cart-notification'
  ].join(',');

  /* Anything inside a reveal group that is sticky / fixed must not get a
     transformed ancestor - those groups only fade. */
  var FIXED_HINT = [
    '[class*="sticky"]', '[class*="toast"]', '[class*="popup"]',
    '[class*="modal"]', '[class*="dialog"]', '[class*="drawer"]'
  ].join(',');

  /* Per-section reveal switches (left untouched if a merchant turns one on) */
  var OWN_REVEAL_ATTRS = [
    'data-aa-reveal', 'data-ab-reveal', 'data-dcb-reveal', 'data-bac-reveal',
    'data-cf-reveal', 'data-nc-reveal', 'data-dpd-reveal', 'data-dpj-reveal',
    'data-fq-reveal', 'data-fd-reveal', 'data-hh-reveal', 'data-lm-reveal',
    'data-mvc-reveal', 'data-op-reveal', 'data-pv-reveal', 'data-rph-reveal',
    'data-tb-reveal', 'data-um-reveal', 'data-wc-reveal', 'data-ait-reveal',
    'data-bc-reveal', 'data-reveal', 'data-button-reveal'
  ];

  var STAGGER = 75;          /* ms, matches --dp-stagger */
  var STAGGER_CAP = 6;       /* never more than ~450ms of waiting */
  var DURATION = 600;        /* ms, matches --dp-dur-reveal */

  /* ------------------------------------------------------------------------
     Helpers
     ------------------------------------------------------------------------ */
  function hasOwnReveal(root) {
    var nodes = [root].concat(Array.prototype.slice.call(root.querySelectorAll('[class]'), 0, 6));
    for (var n = 0; n < nodes.length; n++) {
      var el = nodes[n];
      if (!el.getAttribute) continue;
      for (var i = 0; i < OWN_REVEAL_ATTRS.length; i++) {
        var v = el.getAttribute(OWN_REVEAL_ATTRS[i]);
        if (v !== null && v !== '' && v !== 'none' && v !== 'false') return true;
      }
    }
    return false;
  }

  function isSkippable(el) {
    if (!el || el.nodeType !== 1) return true;
    var tag = el.tagName;
    if (tag === 'STYLE' || tag === 'SCRIPT' || tag === 'TEMPLATE' || tag === 'LINK' || tag === 'NOSCRIPT') return true;
    if (el.hidden || el.hasAttribute('data-dp-rv')) return true;
    var cs = window.getComputedStyle(el);
    if (cs.display === 'none' || cs.position === 'absolute' || cs.position === 'fixed' || cs.position === 'sticky') return true;
    return false;
  }

  function findInner(root) {
    var kids = root.children;
    for (var i = 0; i < kids.length; i++) {
      var list = kids[i].classList;
      for (var j = 0; j < list.length; j++) {
        if (/__(inner|outer|content|layout|frame|card)$/.test(list[j])) return kids[i];
      }
    }
    return null;
  }

  /* Nearest clipping / scrolling ancestor (a slider viewport) inside the section */
  function clipParent(item, stop) {
    var el = item.parentElement;
    var depth = 0;
    while (el && el !== stop && depth < 4) {
      var cs = window.getComputedStyle(el);
      if (cs.overflowX !== 'visible' || el.scrollWidth > el.clientWidth + 2) return el;
      el = el.parentElement;
      depth++;
    }
    return null;
  }

  function belowFold(el) {
    var r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return false;
    return r.top > window.innerHeight * 0.9;
  }

  /* ------------------------------------------------------------------------
     Reveal engine
     ------------------------------------------------------------------------ */
  var io = null;
  var pending = [];

  function finish(el) {
    el.removeAttribute('data-dp-rv');
    el.classList.remove('dp-in');
    el.style.removeProperty('--dp-rv-delay');
  }

  function show(el, delay) {
    if (!el.hasAttribute('data-dp-rv') || el.classList.contains('dp-in')) return;
    el.style.setProperty('--dp-rv-delay', (delay || 0) + 'ms');
    /* next frame so the hidden state is painted first */
    window.requestAnimationFrame(function () {
      el.classList.add('dp-in');
      window.setTimeout(function () { finish(el); }, DURATION + (delay || 0) + 80);
    });
  }

  function onIntersect(entries) {
    var batch = [];
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      io.unobserve(entry.target);
      batch.push(entry.target);
    });
    if (!batch.length) return;

    batch.sort(function (a, b) {
      var pos = a.compareDocumentPosition(b);
      return pos & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
    });

    var step = 0;
    batch.forEach(function (target) {
      var group = target.__dpGroup;
      if (group) {
        /* A slider viewport: reveal its items together, softly staggered */
        group.forEach(function (item, i) { show(item, Math.min(step + i, STAGGER_CAP) * STAGGER); });
        step += Math.min(group.length, 2);
        target.__dpGroup = null;
      } else {
        show(target, Math.min(step, STAGGER_CAP) * STAGGER);
        step++;
      }
    });
  }

  function mark(el, mode) {
    el.setAttribute('data-dp-rv', mode);
    pending.push(el);
  }

  /* Split a container into reveal groups + repeated items */
  function plan(container, depth, out) {
    var kids = container.children;
    for (var i = 0; i < kids.length; i++) {
      var child = kids[i];
      if (isSkippable(child)) continue;
      if (child.matches(ITEM)) {
        out.items.push(child);
      } else if (depth < 3 && child.querySelector(ITEM)) {
        plan(child, depth + 1, out);
      } else {
        out.groups.push(child);
      }
    }
    return out;
  }

  function prepareSection(section) {
    if (!section || section.__dpMotion) return;
    section.__dpMotion = true;

    if (section.matches(SKIP_SECTION) || section.querySelector(SKIP_SECTION)) return;

    var root = section.firstElementChild;
    while (root && isSkippable(root) && root.nextElementSibling) root = root.nextElementSibling;
    if (!root || isSkippable(root)) return;
    if (hasOwnReveal(root)) return;
    if (section.getBoundingClientRect().height < 8) return;

    var inner = findInner(root) || root;
    var result = plan(inner, 0, { groups: [], items: [] });

    /* No repeated items and a busy layout: reveal the content as one group */
    if (!result.items.length && (result.groups.length === 0 || result.groups.length > 4)) {
      result.groups = [inner === root ? root : inner];
    }

    /* Groups (heading, copy, CTA, media) */
    result.groups.forEach(function (group) {
      if (!belowFold(group)) return;
      var fadeOnly = group === root || !!group.querySelector(FIXED_HINT);
      mark(group, fadeOnly ? 'fade' : 'up');
      io.observe(group);
    });

    /* Repeated items */
    var tracks = [];
    result.items.forEach(function (item) {
      if (!belowFold(item) && !clipParent(item, section)) return;
      var track = clipParent(item, section);
      if (track) {
        if (!belowFold(track)) return;           /* slider already visible */
        if (tracks.indexOf(track) === -1) { tracks.push(track); track.__dpGroup = []; }
        mark(item, 'up');
        track.__dpGroup.push(item);
      } else {
        mark(item, 'up');
        io.observe(item);
      }
    });
    tracks.forEach(function (track) { io.observe(track); });
  }

  function revealAll() {
    pending.forEach(function (el) { if (el.hasAttribute('data-dp-rv')) finish(el); });
    pending = [];
  }

  function initReveal() {
    if (reduceMotion || designMode || !('IntersectionObserver' in window)) return;
    var main = document.getElementById('MainContent');
    if (!main) return;

    docEl.classList.add('dp-motion');
    io = new IntersectionObserver(onIntersect, { rootMargin: '0px 0px -8% 0px', threshold: 0 });

    Array.prototype.forEach.call(main.querySelectorAll(':scope > .shopify-section'), prepareSection);

    /* Defensive: anything in view that the observer somehow missed */
    window.setTimeout(function () {
      pending.forEach(function (el) {
        if (!el.hasAttribute('data-dp-rv') || el.classList.contains('dp-in')) return;
        var r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) show(el, 0);
      });
    }, 4000);

    /* Printing / saving a page must never show blank areas */
    window.addEventListener('beforeprint', revealAll);
  }

  /* ------------------------------------------------------------------------
     Cart feedback - bump the header badge when the count increases.
     Scoped to the header only; never touches <html>/<body>.
     ------------------------------------------------------------------------ */
  var badgeObserver = null;

  function bump(el, cls) {
    el.classList.remove(cls);
    void el.offsetWidth; /* restart the animation */
    el.classList.add(cls);
    window.setTimeout(function () { el.classList.remove(cls); }, 600);
  }

  function bindBadge() {
    if (badgeObserver) { badgeObserver.disconnect(); badgeObserver = null; }
    if (!('MutationObserver' in window)) return;
    var header = document.querySelector('.site-header-dr-patkars');
    if (!header) return;
    var badge = header.querySelector('.site-header-dr-patkars__badge');
    if (!badge || badge === docEl || badge === document.body) return;
    var icon = badge.closest('.site-header-dr-patkars__icon-btn');
    var last = parseInt(badge.textContent, 10) || 0;

    badgeObserver = new MutationObserver(function () {
      var now = parseInt(badge.textContent, 10) || 0;
      if (now > last && !reduceMotion) {
        bump(badge, 'dp-bump');
        if (icon) bump(icon, 'dp-bump-icon');
      }
      last = now;
    });
    badgeObserver.observe(badge, { childList: true, characterData: true, subtree: true });
  }

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */
  function boot() {
    initReveal();
    bindBadge();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  /* Theme editor: reloaded sections are shown as-is (no hiding while editing) */
  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (target && target.querySelector && target.querySelector('.site-header-dr-patkars')) bindBadge();
    if (target && target.querySelectorAll) {
      Array.prototype.forEach.call(target.querySelectorAll('[data-dp-rv]'), finish);
    }
  });

  /* Back/forward cache: never restore a half-revealed page */
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) revealAll();
  });

  window.DPMotion = { revealAll: revealAll, version: '1.0.0' };
})();
